/*
 * Decléor — The Gilded Ladder (cart-drawer reward progress step)
 * ---------------------------------------------------------------------------
 * Enhances snippets/cart-reward-ladder.liquid. A pure CONSUMER of the theme's cart events:
 *   theme:cart:change / theme:cart:init carry event.detail.cart (the /cart.js object;
 *   total_price in CENTS, item_count = number of items). We recompute and drive everything
 *   through CSS custom properties (--reward-fill) and state classes — the JS never animates
 *   imperatively except the count-up tween and celebration particles, so the single
 *   prefers-reduced-motion CSS override stays authoritative.
 *
 * Each tier has a GOAL TYPE: "amount" (compared to cart total, in the store currency, ×rate for
 * multi-currency) or "quantity" (compared to item_count). Units can be mixed, so tiers are NOT
 * value-sorted — they keep their entry order (the merchant enters them in increasing order). Each
 * tier's "reached" is evaluated in its own unit; the fill front is sequential (stops at the first
 * unmet gate); medallions check off per their own goal.
 *
 * No-ops gracefully when [data-reward-progress] is absent. Money is formatted with a compact
 * Shopify formatMoney built from window.theme.moneyFormat.
 */
(function () {
  'use strict';

  var SEL = '[data-reward-progress]';
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  function reduce() { return reduceMQ.matches; }

  // ---- Money / formatting -----------------------------------------------
  function formatMoney(cents, format) {
    var fmt = format || '{{amount}}';
    var re = /\{\{\s*(\w+)\s*\}\}/;
    function fmtNum(number, precision, thousands, decimal) {
      thousands = thousands || ','; decimal = decimal || '.';
      if (isNaN(number) || number == null) { return '0'; }
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    var m = fmt.match(re);
    if (!m) { return fmt; }
    var value;
    switch (m[1]) {
      case 'amount': value = fmtNum(cents, 2); break;
      case 'amount_no_decimals': value = fmtNum(cents, 0); break;
      case 'amount_with_comma_separator': value = fmtNum(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmtNum(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = fmtNum(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = fmtNum(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = fmtNum(cents, 2, "'", '.'); break;
      default: value = fmtNum(cents, 2);
    }
    return fmt.replace(re, value);
  }
  function money(cents, root) {
    var fmt = (window.theme && window.theme.moneyFormat) || '{{amount}} €';
    var s = formatMoney(Math.round(cents), fmt);
    if (root.getAttribute('data-reward-ccy') === '1') {
      var code = window.theme && window.theme.currencyCode;
      if (code && s.indexOf(code) === -1) { s = s + ' ' + code; }
    } else if (!/amount_no_decimals/.test(fmt)) {
      // mirror money_without_trailing_zeros, but ONLY for 2-decimal formats — on a no-decimals
      // format a trailing [.,]0+ is a thousands group (1.000 €), not a fraction to strip.
      s = s.replace(/[.,]0+(?=\D*$)/, '');
    }
    return s;
  }
  function rate() {
    var r = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    return r && !isNaN(r) ? r : 1;
  }
  function qtyLabel(root) { return root.getAttribute('data-reward-qty-label') || 'produits'; }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  // The remaining value for a tier, formatted in its unit (cents→money, count→"N produits").
  function formatRemaining(t, raw, root) {
    if (t.type === 'quantity') { return Math.max(0, Math.round(raw)) + ' ' + qtyLabel(root); }
    return money(raw, root);
  }

  // ---- Tiers / maths -----------------------------------------------------
  function getTiers(root) {
    var r = rate();
    return Array.prototype.slice.call(root.querySelectorAll('[data-reward-node]'))
      .map(function (el) {
        var type = el.getAttribute('data-reward-type') === 'quantity' ? 'quantity' : 'amount';
        var raw = parseFloat(el.getAttribute('data-reward-threshold')) || 0;
        return {
          el: el,
          type: type,
          // comparison threshold in the tier's unit: amount = base CENTS × currency rate; quantity = count
          cmp: type === 'quantity' ? raw : Math.round(raw * r),
          label: el.getAttribute('data-reward-label') || ''
        };
      })
      .filter(function (t) { return t.cmp > 0; });
    // NO sort — mixed units can't be value-sorted; entry order is authoritative (merchant enters ascending).
  }
  function curOf(t, state) { return t.type === 'quantity' ? state.items : state.total; }

  // ---- Celebration -------------------------------------------------------
  function intensity(root) {
    var v = parseFloat(getComputedStyle(root).getPropertyValue('--reward-intensity'));
    return isNaN(v) ? 0.6 : Math.max(0, Math.min(1, v));
  }
  function bloom(node) {
    if (reduce()) { return; }
    node.classList.remove('just-unlocked');
    void node.offsetWidth; // restart the animation so a remove→re-add can re-fire it
    node.classList.add('just-unlocked');
    // Remove on a timer keyed to the celebrate duration (NOT the first animationend — the 0.55s ring
    // bloom would otherwise abort the longer gleam/ring-pulse mid-flight). --reward-celebrate-dur inherits.
    var dur = parseFloat(getComputedStyle(node).getPropertyValue('--reward-celebrate-dur')) || 1100;
    if (node._unlockT) { clearTimeout(node._unlockT); }
    node._unlockT = setTimeout(function () { node.classList.remove('just-unlocked'); node._unlockT = null; }, dur + 80);
  }
  function spawnMotes(med, count) {
    for (var i = 0; i < count; i++) {
      var s = document.createElement('span');
      s.className = 'cart-reward__spark';
      var ang = Math.random() * Math.PI * 2;
      var dist = 14 + Math.random() * 16;
      s.style.setProperty('--tx', Math.cos(ang) * dist + 'px');
      s.style.setProperty('--ty', (Math.sin(ang) * dist - 6) + 'px');
      med.appendChild(s);
      s.addEventListener('animationend', function () { this.remove(); });
    }
  }
  // Spawns the celebration MOTES only (sparkle/confetti). The .just-unlocked bloom/gleam is driven
  // separately by bloom(), so the unlock loop can stagger blooms and call this once on the last node
  // without double-blooming it.
  function celebrate(root, node) {
    if (reduce()) { return; }
    var mode = root.getAttribute('data-reward-celebrate') || 'gleam';
    if (mode === 'sparkle' || mode === 'confetti') {
      var med = node.querySelector('.cart-reward__medallion');
      if (med) {
        var n = mode === 'sparkle' ? 3 : Math.max(6, Math.min(20, Math.round(14 * intensity(root))));
        spawnMotes(med, n);
      }
    }
  }
  function haptic(root) {
    if (root.getAttribute('data-reward-haptic') === '1' && !reduce() && navigator.vibrate) {
      try { navigator.vibrate(8); } catch (e) { /* no-op */ }
    }
  }

  // ---- Count-up ----------------------------------------------------------
  function tweenValue(span, fromRaw, toRaw, t, root, cap) {
    var start = null, dur = 600;
    function step(ts) {
      if (start == null) { start = ts; }
      var p = Math.min(1, (ts - start) / dur);
      var eased = 1 - Math.pow(1 - p, 3);
      span.textContent = formatRemaining(t, fromRaw + (toRaw - fromRaw) * eased, root);
      if (p < 1) { requestAnimationFrame(step); }
      else { span.textContent = formatRemaining(t, toRaw, root); if (cap) { cap.setAttribute('aria-live', 'polite'); } }
    }
    requestAnimationFrame(step);
  }

  // ---- Caption -----------------------------------------------------------
  function setCaption(root, tiers, state, allReached, targetIdx, isInit, maxAmountCmp) {
    var cap = root.querySelector('[data-reward-caption]');
    if (!cap) { return; }
    var html, remaining = null, tt = null;
    if (allReached) {
      var over = root.getAttribute('data-reward-overmax-msg');
      var all = root.getAttribute('data-reward-all-msg') || '';
      var useOver = over && maxAmountCmp > 0 && state.total > maxAmountCmp;
      html = escapeHtml(useOver ? over : all);
    } else {
      tt = tiers[targetIdx];
      remaining = Math.max(0, tt.cmp - curOf(tt, state));
      html = escapeHtml(root.getAttribute('data-reward-spend-template') || '')
        .replace('||amount||', '<span class="cart-reward__amount" data-reward-amount>' + formatRemaining(tt, remaining, root) + '</span>')
        .replace('||reward||', '<span class="cart-reward__reward">' + escapeHtml(tt.label) + '</span>');
    }
    cap.innerHTML = html;

    // Count-up only when the SAME target tier's remaining changed (skip when the target tier — and so
    // possibly the unit — changed, otherwise we'd tween across incompatible units).
    var sameTarget = root._rewardTargetIdx === targetIdx;
    root._rewardTargetIdx = targetIdx;
    var countOn = root.getAttribute('data-reward-countup') === '1';
    if (remaining != null && countOn && !reduce() && !isInit && sameTarget &&
        root._rewardRemaining != null && root._rewardRemaining !== remaining) {
      var span = cap.querySelector('[data-reward-amount]');
      if (span) {
        cap.setAttribute('aria-live', 'off'); // silence per-frame count-up; restored to polite on the settled frame
        tweenValue(span, root._rewardRemaining, remaining, tt, root, cap);
      }
    }
    root._rewardRemaining = remaining;
  }

  // ---- Sheen / idle nudge ------------------------------------------------
  function refreshSheen(root, fill) {
    var on = root.getAttribute('data-reward-sheen') === '1' && !reduce() && fill > 0 && fill < 1;
    root.classList.toggle('cart-reward--sheen-on', on);
  }
  function scheduleNudge(root, tiers, targetIdx) {
    if (root._nudgeT) { clearTimeout(root._nudgeT); root._nudgeT = null; }
    if (root.getAttribute('data-reward-idle') !== '1' || reduce() || targetIdx < 0) { return; }
    var delay = (parseFloat(root.getAttribute('data-reward-idle-delay')) || 6) * 1000;
    var node = tiers[targetIdx].el;
    root._nudgeT = setTimeout(function () { node.classList.add('is-nudge'); }, delay);
  }

  // ---- Empty toggle ------------------------------------------------------
  function toggleEmpty(root, ev) {
    if (root.getAttribute('data-reward-empty') !== 'hide') { return; }
    var count = ev && ev.detail && ev.detail.cart ? ev.detail.cart.item_count : null;
    if (count == null) { return; }
    root.classList.toggle('cart-reward--hidden', count <= 0);
  }

  // ---- Core update -------------------------------------------------------
  function update(root, state, isInit) {
    var tiers = getTiers(root);
    if (!tiers.length) { return; }
    var N = tiers.length, seg = 1 / N;

    // 1) Per-tier reached state (each in its own unit) + unlock detection + max amount goal.
    var allReached = true, maxAmountCmp = 0, newlyUnlocked = [];
    for (var k = 0; k < N; k++) {
      var t = tiers[k];
      if (t.type === 'amount' && t.cmp > maxAmountCmp) { maxAmountCmp = t.cmp; }
      var reached = curOf(t, state) >= t.cmp;
      t.el.classList.toggle('is-reached', reached);
      t.el.classList.remove('is-target', 'is-nudge');
      if (!reached) { allReached = false; }
      if (!isInit && reached && t.el.__reached === false) { newlyUnlocked.push(t); }
      t.el.__reached = reached;
    }

    // 2) Sequential fill front (stops at the first unmet gate) + the target node.
    var fill = 0, targetIdx = -1;
    for (var j = 0; j < N; j++) {
      var cur = curOf(tiers[j], state), cmp = tiers[j].cmp;
      if (cur >= cmp) { fill += seg; }
      else { fill += seg * Math.max(0, Math.min(1, cur / cmp)); targetIdx = j; break; }
    }
    if (targetIdx === -1) { fill = 1; }
    fill = Math.max(0, Math.min(1, fill));
    root.style.setProperty('--reward-fill', fill.toFixed(4));
    if (targetIdx >= 0) { tiers[targetIdx].el.classList.add('is-target'); }

    root.classList.toggle('cart-reward--maxed', allReached);
    if (root._collapseT) { clearTimeout(root._collapseT); root._collapseT = null; }
    if (!allReached) { root.classList.remove('cart-reward--collapsed'); }

    setCaption(root, tiers, state, allReached, targetIdx, isInit, maxAmountCmp);

    if (!isInit && newlyUnlocked.length) {
      var lastIdx = newlyUnlocked.length - 1;
      newlyUnlocked.forEach(function (t2, i) {
        (function (node, d, last) {
          setTimeout(function () { bloom(node); if (last) { celebrate(root, node); } }, d);
        })(t2.el, i * 60, i === lastIdx);
      });
      haptic(root);
      if (allReached && root.getAttribute('data-reward-collapse') === '1' && !reduce()) {
        root._collapseT = setTimeout(function () { root._collapseT = null; root.classList.add('cart-reward--collapsed'); }, 2200);
      }
    }

    refreshSheen(root, fill);
    scheduleNudge(root, tiers, targetIdx);
  }

  function readState(root, ev) {
    var c = ev && ev.detail && ev.detail.cart;
    return {
      total: c && typeof c.total_price === 'number' ? c.total_price : (parseInt(root.getAttribute('data-reward-total'), 10) || 0),
      items: c && typeof c.item_count === 'number' ? c.item_count : (parseInt(root.getAttribute('data-reward-items'), 10) || 0)
    };
  }

  // ---- Init / coalesced scheduling --------------------------------------
  var pending = null, pendingRoot = null, pendingState = null;
  function schedule(root, state) {
    pendingRoot = root; pendingState = state;
    if (pending) { return; }
    pending = requestAnimationFrame(function () {
      pending = null;
      update(pendingRoot, pendingState, false);
    });
  }

  function init() {
    var root = document.querySelector(SEL);
    if (!root || root.getAttribute('data-reward-initialized') === '1') { return root; }
    root.setAttribute('data-reward-initialized', '1');
    if (reduce()) { root.style.setProperty('--reward-fill-dur', '0ms'); }
    var tiers = getTiers(root);
    root.style.setProperty('--reward-seg', String(tiers.length)); // segmented layout divisions
    var state0 = {
      total: parseInt(root.getAttribute('data-reward-total'), 10) || 0,
      items: parseInt(root.getAttribute('data-reward-items'), 10) || 0
    };
    tiers.forEach(function (t) { t.el.__reached = curOf(t, state0) >= t.cmp; }); // seed: no celebrate on first paint
    root._rewardRemaining = null;
    root._rewardTargetIdx = -1;
    root.style.setProperty('--reward-fill', '0'); // animate 0 → target on first reveal
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { update(root, state0, true); });
    });
    return root;
  }

  document.addEventListener('theme:cart:change', function (e) {
    var root = init() || document.querySelector(SEL);
    if (!root) { return; }
    toggleEmpty(root, e);
    schedule(root, readState(root, e));
  });
  document.addEventListener('theme:cart:init', function (e) {
    var root = init() || document.querySelector(SEL);
    if (!root) { return; }
    toggleEmpty(root, e);
    schedule(root, readState(root, e));
  });
  // Theme editor: re-init on section reload (idempotent via the initialized flag we clear here).
  document.addEventListener('shopify:section:load', function () {
    var root = document.querySelector(SEL);
    if (root) { root.removeAttribute('data-reward-initialized'); init(); }
  });
  if (reduceMQ.addEventListener) {
    reduceMQ.addEventListener('change', function () {
      var root = document.querySelector(SEL);
      if (!root) { return; }
      root.style.setProperty('--reward-fill-dur', reduce() ? '0ms' : '');
      var fill = parseFloat(getComputedStyle(root).getPropertyValue('--reward-fill')) || 0;
      refreshSheen(root, fill);
    });
  }

  if (document.readyState !== 'loading') { init(); }
  else { document.addEventListener('DOMContentLoaded', init); }
})();
